import { useState, useEffect } from 'react';
import { useAppStore } from '@/lib/store';
import { useAuth, API_URL } from '@/contexts/AuthContext';
import { X, User, Sparkles, Loader2, LogOut, BookOpen } from 'lucide-react';

interface ReaderSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface ReadingOption {
    id: string;
    title: string;
}

export function ReaderSettingsModal({ isOpen, onClose }: ReaderSettingsModalProps) {
    const { themeProfile } = useAppStore();
    const { user, refreshAuth, logout } = useAuth();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [bio, setBio] = useState('');
    const [avatarUrl, setAvatarUrl] = useState('');
    const [readingWorkId, setReadingWorkId] = useState('');
    const [readingOptions, setReadingOptions] = useState<ReadingOption[]>([]);
    const [error, setError] = useState('');

    useEffect(() => {
        const hydrateProfile = async () => {
            if (!isOpen || !user) return;

            setLoading(true);
            setError('');
            setDisplayName(user.pseudonym);
            setBio('');
            setAvatarUrl('');
            setReadingWorkId('');
            setReadingOptions([]);

            try {
                const profileRes = await fetch(`${API_URL}/api/users/${user.sub}/profile`);
                if (profileRes.ok) {
                    const profile = await profileRes.json();
                    setDisplayName(profile.pseudonym || user.pseudonym);
                    setBio(profile.bio || '');
                    setAvatarUrl(profile.avatarUrl || '');
                    setReadingWorkId(profile.readingWorkId || '');
                }

                const libraryRes = await fetch(`${API_URL}/api/users/${user.sub}/library`);
                if (libraryRes.ok) {
                    const library = await libraryRes.json() as { work_id: string }[];
                    const workIds = library.map((entry) => entry.work_id).filter(Boolean).slice(0, 50);
                    const resolved = await Promise.all(workIds.map(async (workId) => {
                        try {
                            const workRes = await fetch(`${API_URL}/api/works/${workId}`);
                            if (!workRes.ok) return null;
                            const payload = await workRes.json();
                            const work = payload.work;
                            return {
                                id: String(work.id),
                                title: String(work.title || work.workTitle || work.id),
                            } satisfies ReadingOption;
                        } catch {
                            return null;
                        }
                    }));

                    const options = resolved.filter((item): item is ReadingOption => Boolean(item));
                    setReadingOptions(options);
                }
            } catch (e: any) {
                setError(e.message || 'Failed to load profile settings.');
            } finally {
                setLoading(false);
            }
        };

        hydrateProfile();
    }, [isOpen, user]);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        const cleanName = displayName.trim();
        if (!cleanName) {
            setError("Display name cannot be empty.");
            return;
        }

        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/users/${user?.sub}/profile`, { method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    pseudonym: cleanName,
                    bio: bio.trim(),
                    avatarUrl: avatarUrl.trim() || null,
                    readingWorkId: readingWorkId || null,
                })
            });

            if (!res.ok) {
                const errData = await res.json();
                setError(errData.error || "Failed to update profile.");
                setSaving(false);
                return;
            }

            // Sync the fresh pseudonym into the local React state
            await refreshAuth();
            onClose();
        } catch (err: any) {
            console.error("Error saving profile", err);
            setError(err.message || "Failed to update profile.");
        } finally {
            setSaving(false);
        }
    };

    const handleSignOut = async () => {
        await logout();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={onClose}
            />

            <div
                className="relative w-full max-w-md rounded-2xl p-6 shadow-2xl animate-in zoom-in-95 duration-200"
                style={{
                    backgroundColor: themeProfile.paperSurface,
                    color: themeProfile.text,
                    border: `1px solid ${themeProfile.border}`
                }}
            >
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-literary flex items-center gap-2">
                        <User className="w-5 h-5 opacity-50" />
                        Reader Profile
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    >
                        <X className="w-5 h-5 opacity-50" />
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-6">
                    <fieldset disabled={loading || saving} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium opacity-70 mb-2">
                                Display Name
                            </label>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder="How should authors know you?"
                                className="w-full px-4 py-3 rounded-xl border focus:outline-none transition-shadow"
                                style={{
                                    backgroundColor: themeProfile.background,
                                    borderColor: themeProfile.border,
                                    color: themeProfile.text,
                                }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium opacity-70 mb-2">
                                Profile Photo URL
                            </label>
                            <input
                                type="url"
                                value={avatarUrl}
                                onChange={(e) => setAvatarUrl(e.target.value)}
                                placeholder="https://example.com/me.jpg"
                                className="w-full px-4 py-3 rounded-xl border focus:outline-none transition-shadow"
                                style={{
                                    backgroundColor: themeProfile.background,
                                    borderColor: themeProfile.border,
                                    color: themeProfile.text,
                                }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium opacity-70 mb-2">
                                Bio
                            </label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                rows={3}
                                placeholder="Tell people a little about your reading taste."
                                className="w-full px-4 py-3 rounded-xl border focus:outline-none resize-none transition-shadow"
                                style={{
                                    backgroundColor: themeProfile.background,
                                    borderColor: themeProfile.border,
                                    color: themeProfile.text,
                                }}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium opacity-70 mb-2">
                                Currently Reading
                            </label>
                            <div className="relative">
                                <BookOpen className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50 pointer-events-none" />
                                <select
                                    value={readingWorkId}
                                    onChange={(e) => setReadingWorkId(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 rounded-xl border focus:outline-none appearance-none"
                                    style={{
                                        backgroundColor: themeProfile.background,
                                        borderColor: themeProfile.border,
                                        color: themeProfile.text,
                                    }}
                                >
                                    <option value="">Nothing selected</option>
                                    {readingOptions.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <label className="block text-sm font-medium opacity-70 mb-2">
                            <span className="flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                This updates your reader profile in My Library.
                            </span>
                        </label>
                    </fieldset>

                    {error && (
                        <div className="p-3 rounded-lg text-sm bg-red-500/10 text-red-500 border border-red-500/20">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-between items-center pt-4 border-t" style={{ borderColor: themeProfile.border }}>
                        <button
                            type="button"
                            onClick={handleSignOut}
                            className="flex items-center gap-2 text-sm font-medium text-red-500/80 hover:text-red-500 transition-colors px-3 py-2 rounded-lg hover:bg-red-500/10"
                        >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                        </button>

                        <button
                            type="submit"
                            disabled={loading || saving || !displayName.trim()}
                            className="px-6 py-2 rounded-full font-medium transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:pointer-events-none flex items-center gap-2"
                            style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                        >
                            {(saving || loading) ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
